import {Component, inject, Inject, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {PatientModel} from '../../models/patient/patient.model';
import {forkJoin, Subject, takeUntil} from 'rxjs';
import {PatientService} from '../../services/patient/patient.service';
import {RecipeService} from '../../services/recipe/recipe.service';
import {DatePipe, NgClass} from '@angular/common';
import {RecipeModel} from '../../models/recipe/recipe.model';
import {iResponse, iResponseList} from '../../models/response/response.model';
import {ReservedAppointmentModel} from "../../models/appointment/appointment.model";
import {FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatInput} from "@angular/material/input";
import {NoteModel, PatientFile, PatientFileModel} from "../../models/patient-file/patient-file.model";
import {PatientFileHandlingComponent} from '../patient/patient-file-handling/patient-file-handling.component';
import {MatCheckbox} from '@angular/material/checkbox';
import {NoteService} from '../../services/notes/note.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {PrescriptionPreviewComponent} from '../modals/prescription-preview/prescription-preview.component';
import {DoctorService} from '../../services/doctor/doctor.service';
import {DoctorModel} from '../../models/doctor/doctor.model';
import {InfoMessageService} from '../../services/infomessage/infomessage.service';
import {BrowserStorageService} from "../../services/browser-storage/browser-storage.service";
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {QueryModel} from '../../services/http/http.service';
import {ImportantNotesComponent} from '../modals/important-notes/important-notes.component';
import {UploadService} from '../../services/upload/upload.service';
import { IdDocumentModel} from '../../models/registration/registration.model';
import {FileUploadComponent} from '../file-upload/file-upload.component';
import {PharmacyModel} from '../../models/pharmacy/pharmacy.model';
import {PharmacyService} from '../../services/pharmacy/pharmacy.service';
import {AppointmentService} from '../../services/appointment/appointment.service';

@Component({
    selector: 'app-patient-file-for-doctor',
    imports: [
        NgClass,
        DatePipe,
        ReactiveFormsModule,
        MatInput,
        PatientFileHandlingComponent,
        FormsModule,
        MatCheckbox,
        MatTabGroup,
        MatTab,
        FileUploadComponent
    ],
    templateUrl: './patient-file-for-doctor.component.html',
    styleUrl: './patient-file-for-doctor.component.sass'
})
export class PatientFileForDoctorComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private patientService = inject(PatientService);
    private recipeService = inject(RecipeService);
    private noteService = inject(NoteService);
    private dialog = inject(MatDialog);
    private doctorService = inject(DoctorService);
    private appointmentService = inject(AppointmentService);
    private infoMessageService = inject(InfoMessageService);
    private browserStorageService = inject(BrowserStorageService);
    private uploadService = inject(UploadService);
    private pharmacyService = inject(PharmacyService);
    private destroy$ = new Subject<void>()
    activePatient!: PatientModel
    patientRecipes: RecipeModel[] = []
    openRecipeIndex: number | null = null
    appointmentForRecipe!: ReservedAppointmentModel

    patientFiles: PatientFileModel[] = [];

    activePatientFile!: PatientFileModel

    isImportantFromControl = new FormControl(false);
    noteFormControl = new FormControl('');

    doctor!: DoctorModel

    idFiles: PatientFile[] = [];
    isLoadingIdFiles: boolean = false;
    showUploadFields: boolean = false;
    idDocuments: IdDocumentModel = {
        front: {
            fileData: '',
            fileName: '',
            mimeType: ''
        },
        back: {
            fileData: '',
            fileName: '',
            mimeType: ''
        }
    }

    customPharmacyForm = new FormGroup({
        name: new FormControl('', Validators.required),
        street: new FormControl('', Validators.required),
        houseNumber: new FormControl('', Validators.required),
        zipCode: new FormControl('', Validators.required),
        city: new FormControl('', Validators.required),
        phoneNumber: new FormControl('', Validators.required),
        email: new FormControl('', Validators.required),
        id: new FormControl(''),
    });

    constructor() {
        const navigation = this.router.currentNavigation();
        const patient = navigation?.extras?.state?.['patient'];
        const appointment = navigation?.extras?.state?.['appointment'];
        if (patient) {
            this.activePatient = patient
        } else if (this.browserStorageService.hasObject('selectedPatient')) {
            const storedPatient = this.browserStorageService.get('selectedPatient');
            if (storedPatient) {
                this.activePatient = JSON.parse(storedPatient);
            }
        }
        if (appointment) {
            this.appointmentForRecipe = appointment
        }

        if (this.activePatient) {
            this.parsePharmacy();
        }

        this.getDoctorDetails();
    }

    ngOnInit(): void {
        if (this.activePatient?.id) {
            this.getPatientFile(this.activePatient.id);
            this.getIdFiles(this.activePatient.id, 'id-documents');
            const query = {
                patientId: this.activePatient.id,
                state: 'draft',
                isEditableByPatient: false
            }

            this.getPatientRecipes(query)
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    toggleRecipe(index: number): void {
        console.log('toggleRecipe called with index:', index)
        this.openRecipeIndex = this.openRecipeIndex === index ? null : index;
    }

    printRecipe(prescriptionId: string): void {
        this.downloadPrescription(prescriptionId)

    }

    private downloadPrescription(id: string): void {
        const query: QueryModel = {
            format: 3
        }
        this.recipeService.downloadPrescription(id, query)
        const patientQuery: QueryModel = {
            patientId: this.activePatient.id
        }
        this.getPatientRecipes(patientQuery)

    }

    releaseRecipe(recipeId: string): void {
        this.saveRecipe(recipeId)
    }

    followRecipe(recipe: RecipeModel, appointment: ReservedAppointmentModel): void {
        this.router.navigate(['rezepte'], {
            state: {
                patient: this.activePatient,
                recipe: recipe,
                appointment: appointment
            }
        })
    }

    updateRecipe(recipe: RecipeModel): void {
        this.router.navigate(['rezepte'], {
            state: {
                patient: this.activePatient,
                recipe: recipe,
                isUpdate: true
            }
        })
    }

    toRecipeCreate(appointment: ReservedAppointmentModel): void {
        this.router.navigate(['rezepte'], {
            state: {
                patient: this.activePatient,
                appointment: appointment
            }
        })
    }

    saveNote(): void {
        if (this.noteFormControl.value === '') {
            return;
        }

        this.postNote(this.noteFormControl.value!, this.isImportantFromControl.value!)
    }

    updateNote(note: NoteModel, noteModel: NgModel, importantNoteModel: NgModel): void {
        this.putNote(note, noteModel, importantNoteModel);

    }

    deleteNote(noteId: string): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            closeOnNavigation: true,
            disableClose: true,
            data: {text: 'Soll die Notiz wirklich gelöscht werden ?', header: 'Löschen', delete: true}
        })

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.delNote(noteId)
            }
        })
    }

    updatePatientFile(): void {
        this.savePatientFile()
    }

    private getPatientFile(patientId: string): void {
        this.patientService.getPatientFiles(patientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PatientFileModel>) => {
                    this.patientFiles = response.data;
                    this.activePatientFile = this.patientFiles[0];
                    this.filterPrescriptionsForDoctor();

                    this.checkPatientNotes()
                },
                error: (error) => {
                }
            })
    }

    private getPatientRecipes(query: QueryModel): void {
        this.recipeService.getRecipes(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<RecipeModel>) => {
                    this.patientRecipes = response.data;
                },
                error: (error) => {
                }
            })
    }

    private saveRecipe(recipeId: string): void {
        this.recipeService.saveRecipe(recipeId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.printRecipe(recipeId)
                },
                error: (error) => {
                }
            })
    }

    private savePatientFile(): void {
        this.patientService.updatePatientFiles(this.activePatient.id, this.activePatientFile)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                },
                error: (error) => {
                }
            })
    }

    private postNote(note: string, isImportant: boolean): void {
        const newNote: NoteModel = {
            note: note,
            isImportant: isImportant
        }

        this.noteService.postNote(this.activePatient.id, this.activePatientFile.id, newNote)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.noteFormControl = new FormControl('');
                    this.isImportantFromControl = new FormControl(false);
                    this.getPatientFile(this.activePatient.id);
                    this.infoMessageService.createMessage('Notiz erfolgreich gespeichert', 'success');
                },
                error: (error) => {
                }
            })
    }

    private putNote(note: NoteModel, noteModel: NgModel, importantNodeModel: NgModel): void {
        this.noteService.putNote(this.activePatient.id, this.activePatientFile.id, note)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.infoMessageService.createMessage('Notiz erfolgreich gespeichert', 'success');
                    noteModel.reset(note.note);
                    importantNodeModel.reset(note.isImportant)

                },
                error: (error) => {
                }
            })
    }

    private delNote(noteId: string): void {
        this.noteService.deleteNote(this.activePatient.id, this.activePatientFile.id, noteId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.getPatientFile(this.activePatient.id);
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    private checkPatientNotes(): void {
        const importantFiles = this.activePatientFile.notes.filter(note => note.isImportant);
        if (importantFiles.length > 0) {
            this.openImportantNoteModal(importantFiles);
        }
    }

    private openImportantNoteModal(importantFiles: NoteModel[]): void {
        const dialogRef = this.dialog.open(ImportantNotesComponent, {
            closeOnNavigation: true,
            disableClose: true,
            data: {notes: importantFiles}
        })
    }

    private filterPrescriptionsForDoctor(): void {
        this.patientRecipes = this.activePatientFile.prescriptions.filter(prescription => {
            return prescription.state === 'published' || prescription.doctor.id === this.doctor.id
        })
    }

    private getDoctorDetails(): void {
        this.doctorService.getMyProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<DoctorModel>) => {
                    this.doctor = response.data;
                    if (this.activePatient?.id) {
                        this.getPatientFile(this.activePatient.id);
                    }

                    this.getAppointments();
                },
                error: (error) => {
                }
            })
    }

    openPrescriptionPreviewModal(prescriptionId: string, published: boolean = false): void {
        const dialogRef = this.dialog.open(PrescriptionPreviewComponent, {
            closeOnNavigation: true,
            disableClose: true,
            data: {prescriptionId: prescriptionId, published: published}
        })

        dialogRef.afterClosed().subscribe(result => {

        })
    }

    onFileSelectedFront(file: any): void {
        this.idDocuments.front = {
            fileData: file.fileData,
            fileName: 'id_front',
            mimeType: file.mimeType
        }
    }

    onFileSelectedBack(file: any): void {
        this.idDocuments.back = {
            fileData: file.fileData,
            fileName: 'id_back',
            mimeType: file.mimeType
        }
    }

    removeFile(type: 'front' | 'back'): void {
        if (type === 'front') {
            this.idDocuments.front = {fileData: '', fileName: '', mimeType: ''};
        } else {
            this.idDocuments.back = {fileData: '', fileName: '', mimeType: ''};
        }
    }

    onSubmitIdDocuments(): void {
        if (this.idDocuments.front.fileData === '' || this.idDocuments.back.fileData === '') {
            this.infoMessageService.createMessage('Bitte beide Seiten des Ausweises hochladen', 'info');
            return;
        }

        const frontFile = this.base64ToFile(this.idDocuments.front.fileData, this.idDocuments.front.fileName, this.idDocuments.front.mimeType);
        const backFile = this.base64ToFile(this.idDocuments.back.fileData, this.idDocuments.back.fileName, this.idDocuments.back.mimeType);

        this.uploadService.uploadIDFiles(frontFile, backFile, this.activePatient.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Ausweisdokumente erfolgreich aktualisiert', 'success');
                    this.showUploadFields = false;
                    this.idDocuments.front = {fileData: '', fileName: '', mimeType: ''};
                    this.idDocuments.back = {fileData: '', fileName: '', mimeType: ''};
                    this.getIdFiles(this.activePatient.id, 'id-documents');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Hochladen der Ausweisdokumente', 'error');
                }
            });
    }

    private getIdFiles(patientId: string, documentTypeId: string): void {
        this.isLoadingIdFiles = true;
        const query: QueryModel = {
            userId: patientId,
            documentType: documentTypeId,
            base64: true
        };
        this.uploadService.getFiles(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PatientFile>) => {
                    this.idFiles = response.data;
                    this.showUploadFields = this.idFiles.length === 0;
                    if (this.idFiles.length > 0) {
                        this.getBase64FilesForIdFiles(patientId);
                    } else {
                        this.isLoadingIdFiles = false;
                    }
                },
                error: (error) => {
                    console.error(error);
                    this.isLoadingIdFiles = false;
                }
            });
    }

    private getBase64FilesForIdFiles(patientId: string): void {
        const queries = this.idFiles.map(file => {
            const query: QueryModel = {
                userId: patientId,
                base64: true
            };
            return this.uploadService.getFile(file.id, query)
                .pipe(takeUntil(this.destroy$));
        });

        if (queries.length === 0) {
            this.isLoadingIdFiles = false;
            return;
        }

        forkJoin(queries).subscribe({
            next: (responses: any[]) => {
                responses.forEach((response, index) => {
                    this.idFiles[index].base46 = response.data;
                });
                this.isLoadingIdFiles = false;
            },
            error: (error) => {
                console.error(error);
                this.isLoadingIdFiles = false;
            }
        });
    }

    private base64ToFile(base64: string, fileName: string, mimeType: string): File {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {type: mimeType});
        return new File([blob], fileName, {type: mimeType});
    }

    triggerDownload(file: PatientFile): void {
        this.uploadService.downloadFile(file.id, file.fileName, file.contentType)
    }

    deleteIdFile(fileId: string): void {
        this.uploadService.deleteFile(fileId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Dokument erfolgreich gelöscht');
                    this.getIdFiles(this.activePatient.id, 'id-documents');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Löschen des Dokuments', 'error');
                }
            });
    }

    private parsePharmacy(): void {
        if (this.activePatient.regularPharmacy) {
            this.customPharmacyForm.get('name')?.patchValue(this.activePatient.regularPharmacy.name);
            this.customPharmacyForm.get('street')?.patchValue(this.activePatient.regularPharmacy.address.street);
            this.customPharmacyForm.get('houseNumber')?.patchValue(this.activePatient.regularPharmacy.address.houseNumber);
            this.customPharmacyForm.get('zipCode')?.patchValue(this.activePatient.regularPharmacy.address.zipCode);
            this.customPharmacyForm.get('city')?.patchValue(this.activePatient.regularPharmacy.address.city);
            this.customPharmacyForm.get('phoneNumber')?.patchValue(this.activePatient.regularPharmacy.phoneNumber);
            if (this.activePatient.regularPharmacy.id) {
                this.customPharmacyForm.get('id')?.patchValue(this.activePatient.regularPharmacy.id);
            }
            if (this.activePatient.regularPharmacy.email) {
                this.customPharmacyForm.get('email')?.patchValue(this.activePatient.regularPharmacy.email);
            }
        }
    }

    savePharmacy(): void {
        if (this.customPharmacyForm.invalid) {
            this.customPharmacyForm.markAllAsTouched();
            this.infoMessageService.createMessage('Bitte füllen sie alle Felder aus', 'error');
            return;
        }

        if (this.activePatient.id) {
            if (this.customPharmacyForm.get('id')?.value !== '') {
                this.updatePharmacy();
            } else {
                const customPharmacy: PharmacyModel = {
                    address: {
                        city: this.customPharmacyForm.get('city')?.value!,
                        houseNumber: this.customPharmacyForm.get('houseNumber')?.value!,
                        street: this.customPharmacyForm.get('street')?.value!,
                        zipCode: this.customPharmacyForm.get('zipCode')?.value!,
                    },
                    email: this.customPharmacyForm.get('email')?.value!,
                    isDefault: true,
                    name: this.customPharmacyForm.get('name')?.value!,
                    phoneNumber: this.customPharmacyForm.get('phoneNumber')?.value!,
                    noSalesTaxes: false,
                    userId: this.activePatient.id,
                }

                this.pharmacyService.createPharmacy(customPharmacy)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.infoMessageService.createMessage('Apotheke erfolgreich gespeichert', 'success');
                            this.activePatient.regularPharmacy = customPharmacy;
                            if (this.browserStorageService.hasObject('selectedPatient')) {
                                this.browserStorageService.set('selectedPatient', this.activePatient);
                            }
                        },
                        error: (error) => {
                            this.infoMessageService.createMessage('Apotheke konnte nicht gespeichert werden', 'error');
                        }
                    })

            }
        }
    }

    private updatePharmacy(): void {
        const updatedPharmacy: PharmacyModel = {
            address: {
                city: this.customPharmacyForm.get('city')?.value!,
                houseNumber: this.customPharmacyForm.get('houseNumber')?.value!,
                street: this.customPharmacyForm.get('street')?.value!,
                zipCode: this.customPharmacyForm.get('zipCode')?.value!,

            },
            email: this.customPharmacyForm.get('email')?.value!,
            isDefault: true,
            name: this.customPharmacyForm.get('name')?.value!,
            phoneNumber: this.customPharmacyForm.get('phoneNumber')?.value!,
            noSalesTaxes: false,
            userId: this.activePatient.id,
            id: this.customPharmacyForm.get('id')?.value!,
        }

        this.pharmacyService.addRegularPharmacy(updatedPharmacy)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Apotheke erfolgreich gespeichert', 'success');
                    this.activePatient.regularPharmacy = updatedPharmacy;
                    if (this.browserStorageService.hasObject('selectedPatient')) {
                        this.browserStorageService.set('selectedPatient', this.activePatient);
                    }
                },
                error: (error) => {
                    this.infoMessageService.createMessage('Apotheke konnte nicht gespeichert werden', 'error');
                }
            })
    }


    private getAppointments(): void {
        const today = new Date();

        const from = new Date(today);
        from.setHours(0, 0, 0, 0);

        const to = new Date(today);
        to.setHours(23, 59, 59, 999);
        const query: QueryModel = {
            isConfirmed: true,
            // doctorId: this.doctor.id,
            from: from.toISOString(),
            to: to.toISOString(),
        };
        this.doctorService.getAppointmentsForDoctor(this.doctor.id, query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (appointments) => {

                    const activeAppointment = appointments.data.filter((appointment) => appointment.patient.id === this.activePatient.id)
                    if (activeAppointment.length > 0) {
                        this.appointmentForRecipe = activeAppointment[0];
                    }
                },
                error: (error) => {
                    this.infoMessageService.createMessage('Termin konnte nicht geladen werden', 'error');
                }
            })
    }
}
